import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Loader from '@components/Loader';
import type { TextModeType } from '@pages/MediaViewer/components/TranscriptDrawer/TranscriptDrawer';

import { buildCaptions, buildTranscript, parseSegments } from './utils';

import './TranscriptionContent.less';

type TranscriptionContentProps = {
  availableTranscripts: Record<string, string>;
  textMode: TextModeType;
  isAutoScrolling: boolean;
  searchValue: string;
};

export default function TranscriptionContent({
  availableTranscripts,
  textMode,
  isAutoScrolling,
  searchValue
}: TranscriptionContentProps) {
  const fileUrl = availableTranscripts.url;
  const { data, isLoading } = useQuery({
    queryKey: ['transcript', fileUrl],
    enabled: !!fileUrl,
    queryFn: async () => {
      const res = await fetch(fileUrl);
      if (!res.ok) {
        throw new Error('Failed to fetch');
      }
      const text = await res.text();
      return parseSegments(text);
    }
  });

  const content = useMemo(() => {
    if (!data) return null;
    if (textMode === 'captions') {
      return buildCaptions(data);
    }
    return buildTranscript(data);
  }, [data, textMode]);

  if (isLoading)
    return <Loader spinSize={72} isSpinning={true} classNameWrapper="loader" />;

  // TODO: add empty state
  if (!content?.length) {
    return (
      <div className="media_viewer_transcription-content__empty">
        No content
      </div>
    );
  }

  return (
    <div className="media_viewer_transcription-content">
      {content.map((item, index) => {
        const time =
          item.start !== item.end ? `${item.start} - ${item.end}` : item.start;
        return (
          <div key={index} className="transcription-block">
            {'id' in item && (
              <div className="transcription-block-number">{item.id}</div>
            )}
            <div className="transcription-block-body">
              <div className="transcription-block-head">
                {'speaker' in item && (
                  <div className="transcription-block-speaker">
                    Speaker {item.speaker}
                  </div>
                )}
                <div className="transcription-block-time">{time}</div>
              </div>
              <div className="transcription-block-text">
                {'sentences' in item &&
                  item.sentences.map((sentence, sentenceIndex) => (
                    <p key={sentenceIndex}>{sentence}</p>
                  ))}
                {'text' in item && <p>{item.text}</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
